import {
  type GetPaginationSessionsParams,
  type SessionsTour,
  useInfiniteScrollFromCache,
  useLazyGetSessionsToursQuery,
} from '@carboncopyco/data';
import { Modal, TextSubHeader } from '@carboncopyco/ui';
import {
  getDateFromDaysAgo,
  getNowInFormat,
  getTimezoneName,
} from '@carboncopyco/utils';
import { useEffect, useMemo } from 'react';
import { FixedTable } from '../../../FixedTable';
import { AssetTableSkeleton } from '../../AssetTableSkeleton';
import { useDashboardProps } from '../../DashboardContext';
import { TourRowWrapper } from './TourRowWrapper';
import { getCols } from './cols';

const cols = getCols(true);

export const ViewAllToursModal = ({ open, onClose }) => {
  const { organizationId, selectedWorkspaceIds, timeRange, short, internal } =
    useDashboardProps();

  const fetchArgs: GetPaginationSessionsParams = useMemo(
    () => ({
      organizationId,
      shortSessions: short,
      internalSessions: internal,
      workspaceIds: selectedWorkspaceIds,
      start: getDateFromDaysAgo(timeRange),
      end: getNowInFormat('YYYY-MM-DDTHH:mm:ssZ'),
      timezone: getTimezoneName(),
      resourceTypes: ['tour'],
    }),
    [organizationId, short, internal, selectedWorkspaceIds, timeRange]
  );

  const {
    items,
    isLoading,
    isLoadingBatch,
    error,
    fetchFirstBatch,
    fetchNextBatch,
    totalSize,
  } = useInfiniteScrollFromCache<SessionsTour, GetPaginationSessionsParams>(
    useLazyGetSessionsToursQuery,
    fetchArgs
  );

  useEffect(() => {
    fetchFirstBatch();
  }, [fetchFirstBatch, fetchArgs]);

  return (
    <Modal
      containerClass="px-0 pb-0"
      floatingCloseButton
      title={
        <div className="flex items-center gap-2 pb-7 px-6">
          <TextSubHeader
            color="text-ds-natural-600"
            label="Tours"
          />
          {totalSize && (
            <TextSubHeader
              label={`(${totalSize})`}
              color="text-ds-natural-300"
            />
          )}
        </div>
      }
      open={open}
      onClose={onClose}
    >
      <div className="overflow-auto">
        <FixedTable<SessionsTour>
          cols={cols}
          data={items}
          isLoading={isLoading}
          loader={<AssetTableSkeleton rows={5} />}
          isLoadingBatch={isLoadingBatch}
          handleFetchNextBatch={fetchNextBatch}
          error={error}
          headerRowClassName="pl-6"
          infiniteListMaxHeight={500}
          rowWrapper={TourRowWrapper}
        />
      </div>
    </Modal>
  );
};
